import { createReducer, on } from '@ngrx/store';
import { authApiActions } from '@core/auth/auth.actions';
import { projectDetailActions } from '@core/actions/project-detail.actions';
import { defaultEntityState, IEntityState } from '@core/state/entities/entity';
import { removeEntitiesByFilter } from '@core/state/entities/entity.helpers';
import { IBulkPdfUploadJob } from '@core/state/entities/bulk-pdf-upload-job/bulk-pdf-upload-job.entity';
import {
  readBulkPdfProjectId,
  readBulkPdfProjectSnapshot,
} from './bulk-pdf-upload-job.snapshot';

interface BulkPdfUploadJobState extends IEntityState<IBulkPdfUploadJob> {
  /** Highest whole-Project audit version reconciled for each project's job membership. */
  bulkPdfProjectSnapshotVersions?: Readonly<Record<string, number>>;
  /** Client-side terminal fence: a deleted Project id can never be resurrected by late payloads. */
  bulkPdfDeletedProjectIds?: Readonly<Record<string, true>>;
}

/**
 * Project payloads can arrive out of order: a normal HTTP response may race a newer SignalR
 * projection. Bulk upload revisions are server-owned, so merge them independently by upload id
 * and never let an older (or merely incomplete) project snapshot regress or remove one. Explicit
 * project deletion remains the sole aggregate-level removal path below.
 */
function mergeMonotonicBulkPdfUploadJobs(
  state: BulkPdfUploadJobState,
  incomingJobs: Record<string, IBulkPdfUploadJob> | null | undefined,
  existingOnly = false
): BulkPdfUploadJobState {
  if (!incomingJobs) {
    return state;
  }

  let byId = state.byId;
  let allIds = state.allIds;
  let changed = false;

  for (const [id, incoming] of Object.entries(incomingJobs)) {
    const existing = state.byId[id];
    if (existingOnly && !existing) {
      continue;
    }
    if (existing && existing.stateRevision >= incoming.stateRevision) {
      continue;
    }

    if (!changed) {
      byId = { ...state.byId };
      allIds = [...state.allIds];
      changed = true;
    }
    byId[id] = incoming;
    if (!existing) {
      allIds.push(id);
    }
  }

  return changed ? { ...state, byId, allIds } : state;
}

function reconcileBulkPdfProjectSnapshot(
  state: BulkPdfUploadJobState,
  props: Parameters<typeof readBulkPdfProjectSnapshot>[0]
): BulkPdfUploadJobState {
  const payloadProjectId = readBulkPdfProjectId(props);
  if (payloadProjectId && state.bulkPdfDeletedProjectIds?.[payloadProjectId]) {
    return state;
  }

  const snapshot = readBulkPdfProjectSnapshot(props);
  if (!snapshot) {
    const projectId = readBulkPdfProjectId(props);
    const hasAcceptedProjectSnapshot =
      projectId !== null &&
      state.bulkPdfProjectSnapshotVersions?.[projectId] !== undefined;
    return mergeMonotonicBulkPdfUploadJobs(
      state,
      props.entities.bulkPdfUploadJobs,
      hasAcceptedProjectSnapshot
    );
  }

  const knownVersion =
    state.bulkPdfProjectSnapshotVersions?.[snapshot.projectId];
  if (
    knownVersion !== undefined &&
    (snapshot.projectVersion < knownVersion ||
      (snapshot.projectVersion === knownVersion && snapshot.authorized))
  ) {
    // A delayed HTTP response may arrive after a newer SignalR projection. Its included jobs can
    // still update retained ids by their own revision, but cannot restore ids that the newer
    // membership/authorization snapshot deliberately pruned.
    return mergeMonotonicBulkPdfUploadJobs(state, snapshot.jobs, true);
  }

  const retainedIds = state.allIds.filter(
    (id) => state.byId[id]?.projectId !== snapshot.projectId
  );
  const byId = Object.fromEntries(
    retainedIds.map((id) => [id, state.byId[id]])
  ) as Record<string, IBulkPdfUploadJob>;
  const incomingIds: string[] = [];

  for (const id of snapshot.jobIds) {
    const incoming = snapshot.jobs[id];
    const existing = state.byId[id];
    const job =
      existing && incoming && existing.stateRevision >= incoming.stateRevision
        ? existing
        : (incoming ?? existing);
    if (!job || job.projectId !== snapshot.projectId) {
      continue;
    }
    byId[id] = job;
    incomingIds.push(id);
  }

  return {
    ...state,
    byId,
    allIds: [...retainedIds, ...incomingIds],
    bulkPdfProjectSnapshotVersions: {
      ...state.bulkPdfProjectSnapshotVersions,
      [snapshot.projectId]: snapshot.projectVersion,
    },
  };
}

/**
 * Jobs are server-owned and read-only on the client, so this reducer only ever merges what
 * project payloads carry. `projectUpdated` is the prompt SignalR path; the exact-session API
 * reconciliation is the bounded fallback when a real-time transition is missed.
 */
export const bulkPdfUploadJobReducer = createReducer(
  defaultEntityState<IBulkPdfUploadJob>(),
  on(
    projectDetailActions.created,
    projectDetailActions.detailLoaded,
    projectDetailActions.projectForInvitationRetrieved,
    projectDetailActions.requestToJoinProjectResponseReceivedAndGranted,
    projectDetailActions.projectUpdated,
    (state, props): IEntityState<IBulkPdfUploadJob> =>
      reconcileBulkPdfProjectSnapshot(state, props)
  ),
  on(
    projectDetailActions.registered,
    (state, props): IEntityState<IBulkPdfUploadJob> =>
      reconcileBulkPdfProjectSnapshot(state, props)
  ),
  on(
    authApiActions.startUserImpersonation,
    authApiActions.stopUserImpersonation,
    (state): BulkPdfUploadJobState => ({
      ...(state as BulkPdfUploadJobState),
      // Project audit versions are only comparable within one effective principal. Keep the
      // read-only entities available for the permission selectors, but make the next payload from
      // the new principal authoritative even when its project version is unchanged.
      bulkPdfProjectSnapshotVersions: undefined,
    })
  ),
  on(
    projectDetailActions.projectDeleted,
    (state, props): IEntityState<IBulkPdfUploadJob> => {
      const bulkState = state as BulkPdfUploadJobState;
      const next = removeEntitiesByFilter(
        bulkState,
        (projChild) => projChild.projectId === props.projectId
      ) as BulkPdfUploadJobState;
      return {
        ...next,
        bulkPdfDeletedProjectIds: props.terminalPhysicalDeletion
          ? {
              ...bulkState.bulkPdfDeletedProjectIds,
              [props.projectId]: true,
            }
          : bulkState.bulkPdfDeletedProjectIds,
      } as BulkPdfUploadJobState;
    }
  )
);
